import { create } from 'zustand';
import type {
  DateTimeRange,
  DateTimeWindow,
} from '@/features/events/lib/range.lib';
import type { PageExperience } from '@/features/experiences/types';

interface PlannerViewRangeStore {
  range: DateTimeRange | undefined;
  contentWindow: DateTimeWindow | undefined;
  experience: PageExperience | undefined;
  child: PageExperience | undefined;
  setRange: (range: DateTimeRange | undefined) => void;
  setContentWindow: (contentWindow: DateTimeWindow | undefined) => void;
  setExperience: (experience: PageExperience | undefined) => void;
  setChild: (child: PageExperience | undefined) => void;
  reset: () => void;
}

// Bridges the active planner child — the loaded child experience and the date
// window it resolved (both owned by ChildPlannerView) — to sibling subtrees that
// need them:
//
// - AddExperienceContent is mounted above ChildContent and otherwise has no
//   access to the viewed range. It needs `contentWindow` as well as `range`:
//   `range` is the canvas, snapped out to whole periods, so judging by it alone
//   calls an event "visible" that the content scope then clips away — see
//   useEventListWindow. Both are published because they answer different
//   questions: what renders (contentWindow) and where the pager could move to
//   (range).
// - ExperiencePlatformBar renders above it too, and only has the parent's
//   embedded child entries (id/type/kind/title/layoutId). Resolving a range from
//   one of those silently disagrees with the child, which reads fields the
//   embedded shape doesn't carry — see useDateRange's callers.
//
// `experience` is the child as rendered, so it includes any active layout
// preview: the bar should follow whatever is actually on screen. It is the PAGER
// experience, whose "What to show" fields are rewritten to the resolved window
// (stamped CUSTOM, because dates are only honored in that mode) — right for the
// bar and for range resolution, wrong for anyone asking what the author chose.
// `child` is that same child unrewritten, for the out-of-range explanations,
// which turn on the mode itself: an UPCOMING view reading as CUSTOM gets told its
// past events are outside the plan rather than that the view shows only what's
// ahead.
//
// The published values outlive any one mount of the content: ChildPlannerView
// republishes on every mount, and PlannerLayout — which stays mounted while the
// events query reloads — is what clears them when the planner is left. Clearing
// them on the content's own unmount instead is what closed the render loop in
// WEB-CLIENT-2BA, since useActiveViewRange keys the shared events fetch on
// `experience` and that fetch's pending state unmounts the publisher.
//
// That field is a bridge around where the bar mounts, not somewhere view state
// belongs. Once the bar renders inside the child's own data scope it can read the
// loaded child from context, and `experience`, `setExperience`, and
// ChildPlannerView's publish effect should all go. `range` stays either way — the
// add-content modal and the export sidebar sit above ChildContent regardless.
const usePlannerViewRangeStore = create<PlannerViewRangeStore>((set) => ({
  range: undefined,
  contentWindow: undefined,
  experience: undefined,
  child: undefined,
  setRange: (range) => set({ range }),
  setContentWindow: (contentWindow) => set({ contentWindow }),
  setExperience: (experience) => set({ experience }),
  setChild: (child) => set({ child }),
  reset: () =>
    set({
      range: undefined,
      contentWindow: undefined,
      experience: undefined,
      child: undefined,
    }),
}));

// Setters only. They are created once with the store, so a caller that just
// publishes (ChildPlannerView) or clears (PlannerLayout) never re-renders on a
// publish — which matters most for the publishers themselves, whose own writes
// would otherwise re-render their whole subtree.
export const usePlannerViewRangeActions = () => {
  const setRange = usePlannerViewRangeStore((state) => state.setRange);
  const setContentWindow = usePlannerViewRangeStore(
    (state) => state.setContentWindow
  );
  const setExperience = usePlannerViewRangeStore(
    (state) => state.setExperience
  );
  const setChild = usePlannerViewRangeStore((state) => state.setChild);
  const reset = usePlannerViewRangeStore((state) => state.reset);

  return { setRange, setContentWindow, setExperience, setChild, reset };
};

// Readers subscribe to every published value; pair it with
// usePlannerViewRangeActions when a caller both reads and writes.
export const usePlannerViewRange = () => {
  const range = usePlannerViewRangeStore((state) => state.range);
  const contentWindow = usePlannerViewRangeStore(
    (state) => state.contentWindow
  );
  const experience = usePlannerViewRangeStore((state) => state.experience);
  const child = usePlannerViewRangeStore((state) => state.child);
  const actions = usePlannerViewRangeActions();

  return {
    range,
    contentWindow,
    experience,
    child,
    ...actions,
  };
};
